import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';
import { CollectionComponent } from './collection/collection.component';
import { MyAhaComponent } from './my-aha/my-aha.component';
import { MyAhaAnonymousComponent } from './my-aha/my-aha-anonymous/my-aha-anonymous.component';
import { SearchPageComponent } from './search-page/search-page.component';
import { LocalStorageKeys } from './shared/LocalStorageKeys';
import { KEYS } from './shared/utils/constants/storage.constants';
import { StorefrontComponent } from './storefront/storefront/storefront.component';
import { CollectionStorefrontComponent } from './storefront/collection-storefront/collection-storefront.component';

interface CustomDetachedRouteHandle extends DetachedRouteHandle {
  componentRef: any;
  contexts: any;
  route: any;
}

export class CustomReuseStrategy implements RouteReuseStrategy {
  components = [MyAhaComponent, SearchPageComponent];
  private routeStore = new Map<string, CustomDetachedRouteHandle>();
  private readonly MAX_CACHED_ROUTES = 4;
  private futureComponent: any = null;

  /**
   * This will be called first while leaving from one route to another to determine whether the component can be detached
   *
   * @param route
   * @returns
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (
      this.restrictCache(route.routeConfig?.component, this.futureComponent)
    ) {
      return false;
    }
    const url = this.getResolvedUrl(route);
    if (
      (url.includes('genres') ||
        url.includes('view-all') ||
        url.includes('artist') ||
        url.includes('collection')) &&
      this.futureComponent === StorefrontComponent
    ) {
      return false;
    }

    // For search component: only cache when navigating forward (e.g., to detail page)
    // Don't cache when navigating to other vertical menu items (MyAha, Storefront)
    if (route.routeConfig?.component === SearchPageComponent) {
      const verticalMenuComponents = [
        MyAhaComponent,
        MyAhaAnonymousComponent,
        StorefrontComponent,
        CollectionStorefrontComponent,
      ];

      // If navigating to a vertical menu component, don't cache search
      if (verticalMenuComponents.includes(this.futureComponent)) {
        // Clear any existing search cache when navigating away to vertical menu
        this.clearRoutesByPattern('search');
        return false; // Don't cache search
      }

      // If navigating forward (e.g., to detail page), cache search
      return true;
    }

    return !!route.data.shouldReuse;
  }

  /**
   * When shouldDetach returned true
   * This method will be called and here we can have store the component instance.
   *
   * @param route
   * @param handle
   */
  store(
    route: ActivatedRouteSnapshot,
    handle: CustomDetachedRouteHandle
  ): void {
    if (handle && route) {
      // Call cleanup() if it exists before storing the component
      if (
        handle?.componentRef?.instance?.cleanup &&
        typeof handle.componentRef.instance.cleanup === 'function'
      ) {
        handle.componentRef.instance.cleanup();
      }
      const key = this.getResolvedUrl(route);
      if (key.includes('//tvmenu/')) {
        // check if the children should be saved
        if (
          route.children.length > 0 &&
          !!!route.children[0].data.shouldReuse
        ) {
          return;
        }
      }

      if (
        typeof handle?.componentRef?.instance?.shouldSave === 'function' &&
        !handle.componentRef.instance.shouldSave()
      ) {
        return;
      }

      /*  // Limit the number of cached routes to prevent memory issues
      if (this.routeStore.size >= this.MAX_CACHED_ROUTES) {
        this.clearOldestRoute();
      } */
      this.routeStore.set(key, handle);
      if (!!route.data.shouldStop) {
        try {
          handle?.componentRef?.instance?.stop();
        } catch (error) {
          console.error(
            '[RS] STOP ERROR: if the component uses the shouldStop flag, should implement the stop function',
            error
          );
        }
      }
    }
  }

  /**
   * This method is used to determine whether the component that we are
   * trying to load should be re-used or not
   *
   * @param route
   * @returns
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (localStorage.getItem(KEYS.FRESH_LAUNCH) === 'true') {
      return false;
    }
    if (!route.routeConfig) return false;

    // For detail routes, always allow attach if shouldReuse is true
    if (route.routeConfig?.path?.includes(':contentType/:id')) {
      return !!route.data.shouldReuse;
    }

    // Don't attach if tab has changed
    /*  if (localStorage.getItem(LocalStorageKeys.TAB_CHANGED) === 'true') {
      return false;
    } */

    const path = this.getResolvedUrl(route);
    const hasStoredRoute = !!this.routeStore.get(path);

    // For storefront routes, check if we're coming from detail
    if (route.routeConfig?.component === StorefrontComponent) {
      const isFromDetail =
        route.parent?.routeConfig?.path?.includes(':contentType/:id');
      if (isFromDetail) {
        // Clear any stored detail routes to prevent back navigation issues
        this.clearRoutesByPattern(':contentType/:id');
        return false;
      }

      // Check if we're navigating back to a storefront
      const currentUrl = window.location.pathname;
      if (
        currentUrl.includes('/tvmenu/') &&
        !currentUrl.includes(':contentType/:id')
      ) {
        return false;
      }

      return hasStoredRoute;
    }

    // For collection storefront routes, check if we're coming from detail
    if (route.routeConfig?.component === CollectionStorefrontComponent) {
      const isFromDetail =
        route.parent?.routeConfig?.path?.includes(':contentType/:id');
      if (isFromDetail) {
        // Clear any stored detail routes to prevent back navigation issues
        this.clearRoutesByPattern(':contentType/:id');
        return false;
      }

      return hasStoredRoute;
    }

    return hasStoredRoute;
  }

  /**
   * When shouldAttach returns true
   * this method will be called, and here we need to retrieve and return the component instance stored
   *
   * @param route
   * @returns
   */
  retrieve(route: ActivatedRouteSnapshot): CustomDetachedRouteHandle {
    if (localStorage.getItem(KEYS.FRESH_LAUNCH) === 'true') {
      this.clearAllCachedRoutes();
      return null;
    }
    if (!route.routeConfig) return null;

    // Clean up routes for special cases
    if (localStorage.getItem(LocalStorageKeys.CLEAN_ROUTES)) {
      this.clearAllCachedRoutes();
      localStorage.removeItem(LocalStorageKeys.CLEAN_ROUTES);
      return undefined;
    }
    if (localStorage.getItem(LocalStorageKeys.CHANGED_LANGUAGE) === 'true') {
      this.clearAllCachedRoutes();
      localStorage.removeItem(LocalStorageKeys.CHANGED_LANGUAGE);
      return undefined;
    }
    if (localStorage.getItem(LocalStorageKeys.CLEAN_SEARCH) === 'true') {
      localStorage.removeItem(LocalStorageKeys.CLEAN_SEARCH);
      this.clearRoutesByPattern('search');
      return undefined;
    }
    /* if (localStorage.getItem(LocalStorageKeys.TAB_CHANGED) === 'true') {
      this.clearAllCachedRoutes();
      localStorage.removeItem(LocalStorageKeys.TAB_CHANGED);
      return undefined;
    } */

    const path = this.getResolvedUrl(route);
    const instance = this.routeStore.get(path);

    if (instance) {
      this.reload(path, instance, route);
    }
    return instance;
  }

  /**
   * This method is used to determine whether the route should be re-used or not
   *
   * @param future
   * @param curr
   * @returns
   */
  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    this.futureComponent = future.routeConfig?.component;
    if (
      this.restrictCache(
        curr.routeConfig?.component,
        future.routeConfig?.component
      )
    ) {
      return false;
    }

    // If the routes are different components, don't reuse
    if (future.routeConfig?.path?.includes('player/:type/:id')) {
      return false;
    }

    if (future.routeConfig?.component !== curr.routeConfig?.component) {
      return false;
    }

    // Don't reuse when navigating between different main components (MyAha, Storefront, etc.)
    const mainComponents = [
      StorefrontComponent,
      MyAhaComponent,
      SearchPageComponent,
    ];
    const futureComponent = future.routeConfig?.component;
    const currComponent = curr.routeConfig?.component;

    if (
      mainComponents.includes(futureComponent) &&
      mainComponents.includes(currComponent) &&
      futureComponent !== currComponent
    ) {
      return false;
    }

    // For storefront routes, only reuse if the IDs match and it's not coming from detail page
    if (future.routeConfig?.component === StorefrontComponent) {
      const isFromDetail = curr.routeConfig?.path?.includes(':contentType/:id');
      const futureId = future.params['id'];
      const currId = curr.params['id'];

      // If we're navigating between storefronts, don't reuse
      if (futureId !== currId) {
        return false;
      }

      // If we're coming from a detail page, don't reuse
      if (isFromDetail) {
        return false;
      }

      // If we're navigating back to a storefront, don't reuse
      const currentUrl = window.location.pathname;
      if (
        currentUrl.includes('/tvmenu/') &&
        !currentUrl.includes(':contentType/:id')
      ) {
        return false;
      }

      return true;
    }

    // For collection storefront routes, only reuse if the collection IDs match
    if (future.routeConfig?.component === CollectionStorefrontComponent) {
      const futureId = future.params['id'];
      const currId = curr.params['id'];

      // If we're navigating between different collections, don't reuse
      if (futureId !== currId) {
        return false;
      }

      // If we're coming from a detail page, don't reuse
      const isFromDetail = curr.routeConfig?.path?.includes(':contentType/:id');
      if (isFromDetail) {
        return false;
      }

      return true;
    }

    // For detail routes, only reuse if the IDs match
    if (future.routeConfig?.path?.includes(':contentType/:id')) {
      return false;
    }

    // For parent routes (like TvVerticalMenuComponent), always reuse
    if (
      future.routeConfig?.component === curr.routeConfig?.component &&
      !future.routeConfig?.path?.includes(':id') &&
      !future.routeConfig?.path?.includes(':contentType/:id')
    ) {
      return true;
    }

    // Default case - reuse if the route configs match
    return future.routeConfig === curr.routeConfig;
  }

  /**
   * Get url with values
   *
   * @param route
   * @returns
   */
  private getResolvedUrl(route: ActivatedRouteSnapshot): string {
    const url = route.pathFromRoot
      .map((v) => v.url.map((segment) => segment.toString()).join('/'))
      .join('/');
    if (url === '//') {
      // tv menu path
      return url + 'tvmenu/' + this.getChildUrl(route);
    }
    return url;
  }

  private getChildUrl(route: ActivatedRouteSnapshot): string {
    return route.children
      .map((v) => v.url.map((segment) => segment.toString()).join('/'))
      .join('/');
  }

  /**
   * Check component type and call the reload methods if is needed
   *
   * @param key
   * @param value
   * @param snapshot
   */
  private reload(key: string, value: any, snapshot: ActivatedRouteSnapshot) {
    if (!!snapshot.data.isStorefront) {
      const component: StorefrontComponent = value?.componentRef?.instance;
      this.reloadStorefront(component, snapshot, key);
    } else if (!!snapshot.data.isMyAha) {
      const component: MyAhaComponent = value?.componentRef?.instance;
      this.reloadMyAha(component, snapshot);
    } else if (value?.componentRef?.instance instanceof SearchPageComponent) {
      const component: SearchPageComponent = value?.componentRef?.instance;
      this.reloadSearchPage(component, snapshot);
    } else if (value?.componentRef?.instance instanceof CollectionComponent) {
      const component: CollectionComponent = value?.componentRef?.instance;
      this.reloadCollection(component, snapshot);
    } else if (
      value?.componentRef?.instance instanceof CollectionStorefrontComponent
    ) {
      const component: CollectionStorefrontComponent =
        value?.componentRef?.instance;
      this.reloadCollectionStorefront(component, snapshot);
    } else {
      // do nothing
    }
  }

  /**
   * Reload storefront component
   *
   * @param component
   * @param snapshot
   */
  private reloadStorefront(
    component: StorefrontComponent,
    snapshot: ActivatedRouteSnapshot,
    key: string
  ): void {
    // validate if storefront was already loaded. If the tab has changed, the storefront must be reloaded
    if (
      !component.saved ||
      localStorage.getItem(LocalStorageKeys.TAB_CHANGED) === 'true'
    ) {
      this.clearSpecificCachedRoutes();
      component.reload(snapshot.data.storefrontData, snapshot.params.id, false);
    }
    // avoid reload twice execution
    component.saved = !component.saved;
  }

  /**
   * Reload storefront component
   *
   * @param component
   * @param snapshot
   */
  private reloadMyAha(
    component: MyAhaComponent,
    snapshot: ActivatedRouteSnapshot
  ): void {
    // validate if storefront was already loaded. If the tab has changed, the storefront must be reloaded
    if (
      !component.saved ||
      localStorage.getItem(LocalStorageKeys.TAB_CHANGED) === 'true'
    ) {
      component.reload(
        localStorage.getItem(LocalStorageKeys.TAB_CHANGED) === 'true'
      );
    }
    // avoid reload twice execution
    component.saved = !component.saved;
  }

  /**
   * Reload search page component
   *
   * @param component
   * @param snapshot
   */
  private reloadSearchPage(
    component: SearchPageComponent,
    snapshot: ActivatedRouteSnapshot
  ): void {
    // Call the restoreFocus method to restore focus after navigation
    if (component && typeof component['restoreFocus'] === 'function') {
      // Use setTimeout to ensure the component is fully rendered
      setTimeout(() => {
        component['restoreFocus']();
      }, 0);
    }
  }

  /**
   * Reload collection component
   *
   * @param component
   * @param snapshot
   */
  private reloadCollection(
    component: CollectionComponent,
    snapshot: ActivatedRouteSnapshot
  ): void {
    // Call the reload method to restore focus after navigation
    if (component && typeof component['reload'] === 'function') {
      // Use setTimeout to ensure the component is fully rendered
      setTimeout(() => {
        component['reload']();
      }, 0);
    }
  }

  /**
   * Reload collection storefront component
   *
   * @param component
   * @param snapshot
   */
  private reloadCollectionStorefront(
    component: CollectionStorefrontComponent,
    snapshot: ActivatedRouteSnapshot
  ): void {
    // Validate if collection storefront was already loaded
    if (!component.saved) {
      const collectionId = snapshot.params?.id;
      if (collectionId) {
        component.reload();
      }
    }
    // Avoid reload twice execution
    component.saved = !component.saved;
  }

  /**
   * Clear all cached routes and properly clean up component instances
   */
  private clearAllCachedRoutes(skipKey?: string): void {
    // Get all keys first to avoid modification during iteration
    const keys = Array.from(this.routeStore.keys());

    // Clean up component instances before clearing the store
    for (const key of keys) {
      if (key !== skipKey) {
        this.clearSingleRoute(key);
      }
    }
  }

  /**
   * Clear cached routes that match a specific pattern
   */
  private clearRoutesByPattern(pattern: string): void {
    // Get all keys first to avoid modification during iteration
    const keys = Array.from(this.routeStore.keys());

    for (const key of keys) {
      if (key.includes(pattern)) {
        this.clearSingleRoute(key);
      }
    }
  }

  /**
   * Clear the oldest route from cache (FIFO approach)
   */
  private clearOldestRoute(): void {
    if (this.routeStore.size > 0) {
      const firstKey = this.routeStore.keys().next().value;
      this.clearSingleRoute(firstKey);
    }
  }

  /**
   * Clear a single route from cache
   */
  private clearSingleRoute(key: string): void {
    const handle = this.routeStore.get(key);
    if (handle) {
      try {
        // Call cleanup() if it exists before destroying the component
        if (
          handle?.componentRef?.instance?.cleanup &&
          typeof handle.componentRef.instance.cleanup === 'function'
        ) {
          handle.componentRef.instance.cleanup();
        }
        // Call stop method if component implements it
        if (
          handle?.componentRef?.instance?.stop &&
          typeof handle.componentRef.instance.stop === 'function'
        ) {
          handle.componentRef.instance.stop();
        }

        // Destroy component reference to help with garbage collection
        if (handle?.componentRef?.destroy) {
          handle.componentRef.destroy();
        }

        // Clear references to help with garbage collection
        handle.componentRef = null;
        handle.contexts = null;
        handle.route = null;
      } catch (error) {
        // Error cleaning up cached route
      }
      this.routeStore.delete(key);
    }
  }

  restrictCache(currComponent: any, futureComponent: any) {
    return (
      this.components.includes(currComponent) &&
      this.components.includes(futureComponent)
    );
  }

  public clearSpecificCachedRoutes(): void {
    const patterns = ['genres', 'view-all', 'artist', 'collection'];
    const keys = Array.from(this.routeStore.keys());

    for (const key of keys) {
      if (patterns.some((pattern) => key.includes(pattern))) {
        this.clearSingleRoute(key);
      }
    }
  }
}
